import React, { useState, ReactNode, cloneElement, isValidElement } from "react";
import { createPortal } from "react-dom";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useClick,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

type Placement = "top" | "bottom" | "left" | "right";

interface PopoverProps {
  reference: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  className?: string;
  id?: string;
  offset?: number;
  trigger?: "click" | "hover";
}

export function Popover({
  reference,
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  placement = "bottom",
  className,
  id,
  offset: offsetPx = 8,
  trigger = "click",
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : uncontrolledOpen;

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open,
    onOpenChange: handleOpenChange,
    middleware: [offset(offsetPx), flip(), shift()],
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
  });

  const click = useClick(context, { enabled: trigger === "click" });
  const hover = useHover(context, { enabled: trigger === "hover", delay: { open: 100, close: 150 } });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss, role]);

  let referenceElement: ReactNode = reference;
  if (isValidElement(reference)) {
    const existingRef: any = (reference as any).ref;
    referenceElement = cloneElement(reference as any, {
      ref: (node: any) => {
        refs.setReference(node);
        if (typeof existingRef === "function") existingRef(node);
        else if (existingRef) (existingRef as React.MutableRefObject<any>).current = node;
      },
      ...(getReferenceProps({
        "aria-expanded": open,
        "aria-controls": id,
      }) as any),
    });
  } else {
    referenceElement = (
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps({
          "aria-expanded": open,
          "aria-controls": id,
        })}
      >
        {String(reference)}
      </button>
    );
  }

  const floatingNode = open
    ? createPortal(
        <div
          id={id}
          role="dialog"
          aria-modal={false}
          ref={refs.setFloating}
          className={className}
          style={{
            ...floatingStyles,
            zIndex: 9999,
          }}
          {...(getFloatingProps() as any)}
        >
          {children}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {referenceElement}
      {floatingNode}
    </>
  );
}

export default Popover;